import { createReducer, on } from '@ngrx/store';
import * as supportActions from '../store/index';
import { AccountTransferIDs, supportInitialState, SupportState } from './support.state';

export const supportReducer = createReducer<SupportState>(
  supportInitialState,
  on(supportActions.actions.supportInit, (): SupportState => {
    return {
      ...supportInitialState
    };
  }),

  on(
    (supportActions.actions.transferStudentPageInit,
    supportActions.actions.transferStudentReset,
    supportActions.actions.transferStudentsSuccess),
    (state): SupportState => {
      return {
        ...state,
        studentTransfer: {
          ...supportInitialState.studentTransfer
        }
      };
    }
  ),

  on(supportActions.actions.studentSearch, (state, action): SupportState => {
    const filteredStudents = state.studentTransfer.cacheStudentTableRows.filter((student) =>
      student.studentName.toLowerCase().includes(action.searchTerm.toLowerCase())
    );

    return {
      ...state,
      studentTransfer: {
        ...state.studentTransfer,
        studentTableRows: filteredStudents
      }
    };
  }),

  //------------------------------------

  on(supportActions.actions.transferinatorAccountDetailsSuccess, (state, action): SupportState => {
    const hasStudents = action.getStudents;

    const cacheStudentTableRows = hasStudents
      ? action.response.studentDetails.map((student) => ({
          id: student.id,
          studentName: student.name
        }))
      : state.studentTransfer.cacheStudentTableRows;

    let accountTransferDetails!: AccountTransferIDs;

    if (hasStudents) {
      accountTransferDetails = {
        ...state.studentTransfer.accountIds,
        fromAccount: {
          id: action.id,
          language: action.response.language,
          accountName: action.response.accountName
        }
      };
    } else {
      accountTransferDetails = {
        ...state.studentTransfer.accountIds,
        toAccount: {
          id: action.id,
          language: action.response.language,
          accountName: action.response.accountName
        }
      };
    }

    return {
      ...state,
      studentTransfer: {
        ...state.studentTransfer,
        cacheStudentTableRows,
        studentTableRows: cacheStudentTableRows,
        accountIds: accountTransferDetails,
        error: ''
      }
    };
  }),

  on(supportActions.actions.transferinatorAccountDetailsFailed, (state, action): SupportState => {
    return {
      ...state,
      studentTransfer: {
        ...state.studentTransfer,
        error: action.error
      }
    };
  }),

  on(supportActions.actions.transferStudentsFailed, (state, action): SupportState => {
    return {
      ...state,

      studentTransfer: {
        ...state.studentTransfer,
        error: action.error as string
      }
    };
  }),

  //------------------------------------
  on(supportActions.actions.accountIDReset, (state, action): SupportState => {
    return {
      ...state,
      studentTransfer: {
        ...state.studentTransfer,
        accountIds: {
          fromAccount: action.fromAccount
            ? { language: '', accountName: '', id: 0 }
            : state.studentTransfer.accountIds.fromAccount,

          toAccount: action.toAccount
            ? { language: '', accountName: '', id: 0 }
            : state.studentTransfer.accountIds.toAccount
        }
      }
    };
  }),

  on(supportActions.actions.cleverSupportPageActions.userSubmittedAccountSearch, (state, action): SupportState => {
    return {
      ...state,
      cleverSupport: {
        ...supportInitialState.cleverSupport,
        accountId: action.accountId,
        loading: true
      }
    };
  }),

  on(supportActions.actions.cleverSupportAPIActions.accountSearchFailure, (state, action): SupportState => {
    return {
      ...state,
      cleverSupport: {
        ...state.cleverSupport,
        loading: false, 
        result: {
          ...supportInitialState.cleverSupport.result
        },
        error: action.error
      }
    };
  }),

  on(supportActions.actions.cleverSupportAPIActions.accountSearchSuccess, (state, action): SupportState => {
    return {
      ...state,
      cleverSupport: {
        ...state.cleverSupport,
        loading: false,
        result: {
          ...action.result,
          isComplete: false
        }
      }
    };
  }),

  on(
    (supportActions.actions.cleverSupportAPIActions.accountSearchFailure,
    supportActions.actions.cleverSupportAPIActions.cleverAccountUpdateFailure),
    (state, action): SupportState => {
      return {
        ...state,
        cleverSupport: {
          ...state.cleverSupport,
          loading: false,
          result: {
            ...supportInitialState.cleverSupport.result
          },
          error: action.error
        }
      };
    }
  ),

  on(supportActions.actions.cleverSupportPageActions.userSubmittedNewCleverDetails, (state): SupportState => {
    return {
      ...state,
      cleverSupport: {
        ...state.cleverSupport,
        loading: true
      }
    };
  }),

  on(supportActions.actions.cleverSupportAPIActions.cleverAccountUpdateSuccess, (state): SupportState => {
    return {
      ...state,
      cleverSupport: {
        ...state.cleverSupport,
        loading: false,
        result: {
          ...state.cleverSupport.result,
          isComplete: true
        }
      }
    };
  }),

  on(
    supportActions.actions.cleverSupportPageActions.cleverSupportInit,
    supportActions.actions.cleverSupportPageActions.userResetAccountSearch,
    (state): SupportState => {
      return {
        ...state,
        cleverSupport: {
          ...supportInitialState.cleverSupport
        }
      };
    }
  ),

  on(supportActions.actions.undeleteSupportPageActions.undeleteSupportInit, (state): SupportState => {
    return {
      ...state,
      undeleteSupport: {
        ...supportInitialState.undeleteSupport
      }
    };
  }),

  on(supportActions.actions.undeleteSupportPageActions.userSubmittedAccountSearch, (state, action): SupportState => {
    return {
      ...state,
      undeleteSupport: {
        ...state.undeleteSupport,
        accountId: action.accountId,
        loading: true
      }
    };
  }),

  on(supportActions.actions.undeleteSupportAPIActions.accountSearchSuccess, (state, action): SupportState => {
    const sortedStudents = [...action.result.students].sort(
      (studentOne, studentTwo) => new Date(studentTwo.deletedAt).getTime() - new Date(studentOne.deletedAt).getTime()
    );

    return {
      ...state,
      undeleteSupport: {
        ...state.undeleteSupport,
        loading: false,
        result: {
          ...action.result,
          students: sortedStudents.map((student) => ({
            ...student,
            deletedAt: formatToLocalDate(student.deletedAt),
            loading: false,
            undeleted: false
          }))
        }
      }
    };
  }),

  on(supportActions.actions.undeleteSupportPageActions.userRequestedStudentUndelete, (state, action): SupportState => {
    return {
      ...state,
      undeleteSupport: {
        ...state.undeleteSupport,
        result: {
          students: state.undeleteSupport.result.students.map((student) => {
            if (student.id === action.studentId) {
              return {
                ...student,
                loading: true
              };
            }
            return student;
          })
        }
      }
    };
  }),

  on(supportActions.actions.undeleteSupportAPIActions.studentRestoreSuccess, (state, action): SupportState => {
    return {
      ...state,
      undeleteSupport: {
        ...state.undeleteSupport,
        result: {
          students: state.undeleteSupport.result.students.map((student) => {
            if (student.id === action.studentId) {
              return {
                ...student,
                loading: false,
                undeleted: true
              };
            }
            return student;
          })
        }
      }
    };
  }),

  on(supportActions.actions.undeleteSupportPageActions.userResetAccountSearch, (state): SupportState => {
    return {
      ...state,
      undeleteSupport: {
        ...supportInitialState.undeleteSupport
      }
    };
  })
);

function getDateNow(existingDate?: Date | string): Date {
  return existingDate ? new Date(existingDate) : new Date();
}

function formatToLocalDate(date: string | Date): string {
  if (!date) return '';
  const parsedDate = getDateNow(date);
  return isNaN(parsedDate.getTime()) ? '' : parsedDate.toLocaleDateString();
}
